import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  appendToList,
  patchShareToken,
  removeFromList,
  replaceInList,
  withDetailRefresh,
} from '@/hooks/binders/binderCache'
import { bindersQueries } from '@/hooks/binders/binderQueries'
import { useDefinedQuery } from '@/hooks/useDefinedQuery'
import { withIdempotency } from '@/hooks/withIdempotency'
import {
  createBinder,
  updateBinder,
  deleteBinder,
  regenerateBinderShareToken,
  addBinderPage,
  removeBinderPage,
} from '@/services/api/binders'
import type { BinderResponse, ShareTokenResponse, BinderLayout, BinderSummary } from '@/types'

// Slot mutations (offline-replay-able) live in binderSlotMutations; re-exported so this stays the
// single public entry point for binder hooks.
export {
  binderMutations,
  useSetBinderSlot,
  useClearBinderSlot,
  useMoveBinderSlot,
} from '@/hooks/binders/binderSlotMutations'

// ---------------------------------------------------------------------------
// Read hooks
// ---------------------------------------------------------------------------

export function useBinders() {
  return useDefinedQuery(bindersQueries.list)
}

/** Total cards placed across every binder — the slot-count reduce shared by BindersListPage. */
export function sumBinderSlots(binders: BinderSummary[] | undefined): number {
  return binders?.reduce((sum, b) => sum + b.slotCount, 0) ?? 0
}

export function useBinder(id: number | undefined) {
  return useDefinedQuery(bindersQueries.detail, id as number, { enabled: id !== undefined })
}

export function useSharedBinder(shareToken: string | undefined) {
  return useDefinedQuery(bindersQueries.shared, shareToken as string, { enabled: shareToken !== undefined })
}

// ---------------------------------------------------------------------------
// CRUD mutations
// ---------------------------------------------------------------------------

export function useCreateBinder() {
  const queryClient = useQueryClient()
  type Vars = {
    data: { name: string; description?: string; layout: BinderLayout; coverColor?: string }
    idempotencyKey?: string
  }
  const mutation = useMutation({
    mutationFn: ({ data, idempotencyKey }: Vars) => createBinder(data, idempotencyKey),
    onSuccess: (resp) => appendToList(queryClient, resp),
  })
  return withIdempotency(mutation)
}

export function useUpdateBinder() {
  const queryClient = useQueryClient()
  type Vars = {
    id: number
    data: { name?: string; description?: string; layout?: BinderLayout; coverColor?: string }
    idempotencyKey?: string
  }
  const mutation = useMutation({
    mutationFn: ({ id, data, idempotencyKey }: Vars) => updateBinder(id, data, idempotencyKey),
    onSuccess: (resp, { id }) => {
      replaceInList(queryClient, resp)
      queryClient.setQueryData<BinderResponse>(bindersQueries.detail.queryKey(id), resp)
    },
  })
  return withIdempotency(mutation)
}

export function useDeleteBinder() {
  const queryClient = useQueryClient()
  type Vars = { id: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, idempotencyKey }: Vars) => deleteBinder(id, idempotencyKey),
    onSuccess: (_void, { id }) => {
      removeFromList(queryClient, id)
      queryClient.removeQueries({ queryKey: bindersQueries.detail.queryKey(id) })
    },
  })
  type PublicVars = number | Vars
  const normalize = (vars: PublicVars): Vars => (typeof vars === 'number' ? { id: vars } : vars)
  return withIdempotency(mutation, normalize)
}

export function useRegenerateBinderToken() {
  const queryClient = useQueryClient()
  type Vars = { id: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, idempotencyKey }: Vars) => regenerateBinderShareToken(id, idempotencyKey),
    onSuccess: (resp: ShareTokenResponse, { id }) => patchShareToken(queryClient, id, resp.shareToken, resp.shareUrl),
  })
  type PublicVars = number | Vars
  const normalize = (vars: PublicVars): Vars => (typeof vars === 'number' ? { id: vars } : vars)
  return withIdempotency(mutation, normalize)
}

// ---------------------------------------------------------------------------
// Page mutations
// ---------------------------------------------------------------------------

export function useAddBinderPage() {
  const queryClient = useQueryClient()
  type Vars = { id: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, idempotencyKey }: Vars) => addBinderPage(id, idempotencyKey),
    onSuccess: (_data, { id }) => withDetailRefresh(queryClient, id),
  })
  type PublicVars = number | Vars
  const normalize = (vars: PublicVars): Vars => (typeof vars === 'number' ? { id: vars } : vars)
  return withIdempotency(mutation, normalize)
}

export function useRemoveBinderPage() {
  const queryClient = useQueryClient()
  type Vars = { id: number; pageNumber: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, pageNumber, idempotencyKey }: Vars) => removeBinderPage(id, pageNumber, idempotencyKey),
    onSuccess: (_void, { id }) => withDetailRefresh(queryClient, id),
  })
  return withIdempotency(mutation)
}
